import { useState, useEffect, useRef } from "react";
import { cn } from "@/lib/utils";
import AnimatedText from "./AnimatedText";
import { useToast } from "@/hooks/use-toast";
import { siteConfig } from "@/config";
import { useSoundEffects } from "@/hooks/useSoundEffects";
import {
  Instagram,
  Youtube,
  MessageCircle,
  Linkedin,
  Twitter,
  Github,
} from "lucide-react";

const Contact = () => {
  const { toast } = useToast();
  const {
    hoverSound,
    clickSound,
    handleTypingSound,
    successSound,
    debouncedHoverSound,
  } = useSoundEffects();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [formState, setFormState] = useState<
    "idle" | "submitting" | "submitted"
  >("idle");
  const [formActive, setFormActive] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const [focusedInput, setFocusedInput] = useState<string | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Use the proper React event handler for typing sounds
    handleTypingSound(e);
  };

  const handleFocus = (name: string) => {
    setFocusedInput(name);
    hoverSound();
  };

  const handleBlur = () => {
    setFocusedInput(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormState("submitting");
    clickSound();

    const now = new Date().toLocaleString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

    // Create a more professionally formatted message for WhatsApp
    const whatsappMessage =
      "*CONTACT INQUIRY FROM WEBSITE*\n\n" +
      "*Name:* " +
      formData.name +
      "\n" +
      "*Email:* " +
      formData.email +
      "\n" +
      "*Phone:* " +
      (formData.phone || "Not provided") +
      "\n" +
      "*Message:* " +
      formData.message +
      "\n" +
      "*Sent on:* " +
      now;

    // Create WhatsApp URL with the phone number from config and the formatted message
    const phoneNumber = siteConfig.contact.phone.replace(/\D/g, "");
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(
      whatsappMessage
    )}`;

    // Show toast notification
    toast({
      title: "Redirecting to WhatsApp",
      description: "You'll be redirected to WhatsApp to send your message.",
    });

    // Small delay before redirecting
    setTimeout(() => {
      // Play success sound
      successSound();

      // Open WhatsApp in a new tab
      window.open(whatsappUrl, "_blank");

      // Reset form
      setFormData({ name: "", email: "", phone: "", message: "" });
      setFormState("idle");
    }, 1000);
  };

  // Function to get the appropriate icon based on social platform name
  const getSocialIcon = (name: string) => {
    switch (name.toLowerCase()) {
      case "github":
        return <Github size={20} />;
      case "linkedin":
        return <Linkedin size={20} />;
      case "twitter":
        return <Twitter size={20} />;
      case "instagram":
        return <Instagram size={20} />;
      case "youtube":
        return <Youtube size={20} />;
      case "whatsapp":
        return <MessageCircle size={20} />;
      default:
        return (
          <span>
            {name.charAt(0)}
            {name.charAt(1)}
          </span>
        );
    }
  };

  return (
    <section id="contact" className="py-20 md:py-32" ref={sectionRef}>
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-16">
          <div
            className={cn(
              "transition-all duration-1000",
              isVisible
                ? "opacity-100 translate-x-0"
                : "opacity-0 -translate-x-10"
            )}
          >
            <h2 className="font-display text-5xl md:text-6xl tracking-tight mb-6">
              <AnimatedText text={siteConfig.contact.title} />
            </h2>
            <p className="mb-8 max-w-md">{siteConfig.contact.description}</p>

            <div className="space-y-4">
              <div
                className="transform transition-all duration-500"
                style={{
                  transitionDelay: "200ms",
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? "translateY(0)" : "translateY(20px)",
                }}
              >
                <h3 className="text-sm font-semibold uppercase tracking-wider mb-1">
                  Email
                </h3>
                <a
                  href={`mailto:${siteConfig.contact.email}`}
                  className="text-lg hover:underline relative inline-block"
                  onMouseEnter={hoverSound}
                  onClick={clickSound}
                >
                  {siteConfig.contact.email}
                  <span className="block h-0.5 w-0 group-hover:w-full bg-white transition-all duration-300"></span>
                </a>
              </div>

              <div
                className="transform transition-all duration-500"
                style={{
                  transitionDelay: "300ms",
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? "translateY(0)" : "translateY(20px)",
                }}
              >
                <h3 className="text-sm font-semibold uppercase tracking-wider mb-1">
                  Phone
                </h3>
                <a
                  href={`tel:${siteConfig.contact.phone}`}
                  className="text-lg hover:underline relative inline-block"
                  onMouseEnter={hoverSound}
                  onClick={clickSound}
                >
                  {siteConfig.contact.phone}
                </a>
              </div>

              <div
                className="pt-4 transform transition-all duration-500"
                style={{
                  transitionDelay: "400ms",
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? "translateY(0)" : "translateY(20px)",
                }}
              >
                <h3 className="text-sm font-semibold uppercase tracking-wider mb-3">
                  Connect
                </h3>
                <div className="flex flex-wrap gap-4">
                  {siteConfig.contact.socialLinks.map((link, index) => (
                    <a
                      key={link.name}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="border border-white w-10 h-10 flex items-center justify-center group relative overflow-hidden"
                      aria-label={link.name}
                      onMouseEnter={debouncedHoverSound}
                      onClick={clickSound}
                    >
                      <span className="relative z-10 transition-colors duration-300 group-hover:text-black">
                        {getSocialIcon(link.name)}
                      </span>
                      <span className="absolute inset-0 bg-white transform scale-x-0 scale-y-0 origin-center group-hover:scale-x-100 group-hover:scale-y-100 transition-transform duration-300"></span>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div
            className={cn(
              "relative transition-all duration-1000",
              isVisible
                ? "opacity-100 translate-x-0"
                : "opacity-0 translate-x-10"
            )}
          >
            <form
              onSubmit={handleSubmit}
              className={cn(
                "transition-all duration-500",
                formActive ? "opacity-100" : "opacity-0"
              )}
            >
              <div className="mb-6 group">
                <label
                  htmlFor="name"
                  className={cn(
                    "block text-sm uppercase tracking-wider mb-2 transition-all duration-300",
                    focusedInput === "name" ? "text-white" : "text-white/70"
                  )}
                >
                  Name
                </label>
                <div className="relative">
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    onFocus={() => handleFocus("name")}
                    onBlur={handleBlur}
                    required
                    className="w-full bg-transparent border-b border-white py-2 px-0 focus:outline-none focus:border-opacity-100 transition-all"
                    disabled={formState !== "idle"}
                  />
                  <span
                    className={cn(
                      "absolute bottom-0 left-0 w-0 h-0.5 bg-white transition-all duration-300",
                      focusedInput === "name" ? "w-full" : "w-0"
                    )}
                  ></span>
                </div>
              </div>

              <div className="mb-6 group">
                <label
                  htmlFor="email"
                  className={cn(
                    "block text-sm uppercase tracking-wider mb-2 transition-all duration-300",
                    focusedInput === "email" ? "text-white" : "text-white/70"
                  )}
                >
                  Email
                </label>
                <div className="relative">
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    onFocus={() => handleFocus("email")}
                    onBlur={handleBlur}
                    required
                    className="w-full bg-transparent border-b border-white py-2 px-0 focus:outline-none focus:border-opacity-100 transition-all"
                    disabled={formState !== "idle"}
                  />
                  <span
                    className={cn(
                      "absolute bottom-0 left-0 w-0 h-0.5 bg-white transition-all duration-300",
                      focusedInput === "email" ? "w-full" : "w-0"
                    )}
                  ></span>
                </div>
              </div>

              <div className="mb-6 group">
                <label
                  htmlFor="phone"
                  className={cn(
                    "block text-sm uppercase tracking-wider mb-2 transition-all duration-300",
                    focusedInput === "phone" ? "text-white" : "text-white/70"
                  )}
                >
                  Phone
                </label>
                <div className="relative">
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    onFocus={() => handleFocus("phone")}
                    onBlur={handleBlur}
                    className="w-full bg-transparent border-b border-white py-2 px-0 focus:outline-none focus:border-opacity-100 transition-all"
                    disabled={formState !== "idle"}
                  />
                  <span
                    className={cn(
                      "absolute bottom-0 left-0 w-0 h-0.5 bg-white transition-all duration-300",
                      focusedInput === "phone" ? "w-full" : "w-0"
                    )}
                  ></span>
                </div>
              </div>

              <div className="mb-8 group">
                <label
                  htmlFor="message"
                  className={cn(
                    "block text-sm uppercase tracking-wider mb-2 transition-all duration-300",
                    focusedInput === "message" ? "text-white" : "text-white/70"
                  )}
                >
                  Message
                </label>
                <div className="relative">
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    onFocus={() => handleFocus("message")}
                    onBlur={handleBlur}
                    required
                    rows={4}
                    className="w-full bg-transparent border-b border-white py-2 px-0 focus:outline-none focus:border-opacity-100 transition-all resize-none"
                    disabled={formState !== "idle"}
                  ></textarea>
                  <span
                    className={cn(
                      "absolute bottom-0 left-0 w-0 h-0.5 bg-white transition-all duration-300",
                      focusedInput === "message" ? "w-full" : "w-0"
                    )}
                  ></span>
                </div>
              </div>

              <button
                type="submit"
                className="w-full border border-white py-3 uppercase text-sm tracking-widest transition-all duration-300 disabled:opacity-50 group relative overflow-hidden"
                disabled={formState !== "idle"}
                onMouseEnter={hoverSound}
              >
                <span className="relative z-10 transition-colors duration-300 group-hover:text-black">
                  {formState === "idle" && "Send via WhatsApp"}
                  {formState === "submitting" && "Preparing Message..."}
                  {formState === "submitted" && "Message Ready"}
                </span>
                <span className="absolute inset-0 bg-white transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></span>
              </button>
            </form>

            <div
              className={cn(
                "absolute inset-0 flex items-center justify-center transition-all duration-500",
                !formActive ? "opacity-100" : "opacity-0 pointer-events-none"
              )}
            >
              <div className="text-center">
                <div className="inline-block w-16 h-16 border-4 border-t-4 border-white rounded-full mb-6 animate-spin"></div>
                <h3 className="font-display text-3xl mb-4 animate-pulse">
                  Message Sent!
                </h3>
                <p
                  className="animate-fade-in"
                  style={{ animationDelay: "500ms" }}
                >
                  Thank you for reaching out. I'll get back to you soon.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
